import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PatientNote, NoteSeverity } from '../../../services/patientNotes';
import {
    RichEditor,
    tiptapDocToPlainText,
    docFromPlainText,
    collectDocImageFileIds,
    applyResolvedUrlsToDoc
} from '@odo/shared-ui';
import type { TiptapDoc } from '@odo/shared-ui';
import { uploadInternalImage, resolveInternalImageUrls } from '../../../services/internalImages';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import {
    SEVERITY,
    MAX_NOTE_TITLE_LENGTH,
    MAX_NOTE_BODY_LENGTH,
    type SeverityKey
} from './severity';

const SEVERITY_KEYS: SeverityKey[] = ['info', 'warning', 'critical'];

const EMPTY_DOC: TiptapDoc = { type: 'doc', content: [] };

function initialDoc(note: PatientNote | null): TiptapDoc {
    if (note?.bodyDoc) {
        return note.bodyDoc;
    }
    if (note?.body) {
        return docFromPlainText(note.body);
    }
    return EMPTY_DOC;
}

interface NoteModalProps {
    patientId: string;
    note: PatientNote | null;
    isSaving: boolean;
    onSave: (data: { severity: NoteSeverity; title: string; bodyDoc: TiptapDoc }) => void;
    onClose: () => void;
}

function NoteModal({ patientId, note, isSaving, onSave, onClose }: NoteModalProps) {
    const [severity, setSeverity] = useState<SeverityKey>(note?.severity ?? 'info');
    const [title, setTitle] = useState(note?.title ?? '');
    const [doc, setDoc] = useState<TiptapDoc>(() => initialDoc(note));
    const titleRef = useRef<HTMLInputElement>(null);

    const isEdit = !!note;
    const plainText = tiptapDocToPlainText(doc).trim();
    const overLimit = plainText.length > MAX_NOTE_BODY_LENGTH;
    const canSave = title.trim().length > 0 && plainText.length > 0 && !overLimit && !isSaving;

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    // When editing a note that already has images, swap the stored (expired)
    // file-id references for freshly-signed URLs so they render in the editor.
    useEffect(() => {
        if (!note?.bodyDoc) {
            return;
        }
        const fileIds = collectDocImageFileIds(note.bodyDoc);
        if (fileIds.length === 0) {
            return;
        }
        let cancelled = false;
        resolveInternalImageUrls(patientId, fileIds)
            .then((urls) => {
                if (!cancelled) {
                    setDoc((d) => applyResolvedUrlsToDoc(d, urls));
                }
            })
            .catch(() => {
                /* leave unresolved — images may render broken, but text is intact */
            });
        return () => {
            cancelled = true;
        };
    }, [note, patientId]);

    useEscapeKey(onClose, !isSaving);

    const handleImageUpload = useCallback(
        async (file: File) => {
            const result = await uploadInternalImage(patientId, file, 'note');
            return { url: result.url, fileId: result.fileId };
        },
        [patientId]
    );

    const handleSave = () => {
        if (!canSave) return;
        onSave({ severity, title: title.trim(), bodyDoc: doc });
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="note-modal-title"
            className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40"
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-[540px] flex flex-col overflow-hidden"
                style={{ maxHeight: '90vh' }}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2
                        id="note-modal-title"
                        className="text-sm font-bold text-gray-900 font-inter"
                    >
                        {isEdit ? 'Edit note' : 'Add note'}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        aria-label="Close"
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-50"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold font-inter text-gray-400 mb-2">
                            Severity
                        </div>
                        <div className="flex gap-2">
                            {SEVERITY_KEYS.map((key) => {
                                const s = SEVERITY[key];
                                const Icon = s.icon;
                                const active = severity === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => setSeverity(key)}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border-2 transition-all ${
                                            active
                                                ? `${s.border} ${s.bg} ${s.iconColor} ring-2 ${s.ring}`
                                                : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300'
                                        }`}
                                    >
                                        <span className={active ? s.iconColor : 'text-gray-400'}>
                                            <Icon className="w-4 h-4" />
                                        </span>
                                        <span
                                            className={`text-[12px] font-semibold font-inter ${
                                                active ? s.iconColor : 'text-gray-500'
                                            }`}
                                        >
                                            {s.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold font-inter text-gray-400 mb-1.5">
                            Title
                        </div>
                        <input
                            ref={titleRef}
                            type="text"
                            value={title}
                            maxLength={MAX_NOTE_TITLE_LENGTH}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Short, descriptive title…"
                            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 font-inter placeholder-gray-300 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100"
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[10px] uppercase tracking-wider font-semibold font-inter text-gray-400">
                                Note
                            </div>
                            <span
                                className={`text-[10px] font-inter ${overLimit ? 'text-red-500' : 'text-gray-300'}`}
                            >
                                {plainText.length}/{MAX_NOTE_BODY_LENGTH}
                            </span>
                        </div>
                        <RichEditor
                            value={doc}
                            onChange={setDoc}
                            onImageUpload={handleImageUpload}
                            placeholder="Add context, instructions, or observations…"
                            minHeightPx={140}
                        />
                    </div>
                </div>

                <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-gray-50/50">
                    <button
                        onClick={onClose}
                        className="text-sm font-medium text-gray-500 hover:text-gray-700 font-inter px-3 py-1.5"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!canSave}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold font-inter transition-all ${
                            canSave
                                ? 'bg-gray-900 text-white hover:bg-black shadow-sm'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        {isSaving ? (
                            <>
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-current/30 border-t-current animate-spin" />
                                Saving…
                            </>
                        ) : isEdit ? (
                            'Save changes'
                        ) : (
                            'Add note'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { NoteModal };
